import JsonLd from '@/components/base/JsonLd';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function VsCompetitors() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://allchinabuyspreadsheet.wiki';
  const pagePath = '/allchinabuy-sheet-vs-competitors';
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'AllChinaBuySpreadsheet vs AliExpress, Taobao, DHgate & 1688: Full Breakdown',
    description: 'Comprehensive comparison of AllChinaBuySpreadsheet against AliExpress, Taobao, DHgate, and 1688. Compare ease of use, pricing transparency, trust levels, and spreadsheet support across all major China shopping platforms.',
    author: { '@type': 'Organization', name: 'AllChinaBuySpreadsheet' },
    publisher: { '@type': 'Organization', name: 'AllChinaBuySpreadsheet', url: siteUrl },
    datePublished: '2026-06-25',
    dateModified: '2026-06-25',
    mainEntityOfPage: { '@type': 'WebPage', '@id': siteUrl + pagePath },
    url: siteUrl + pagePath,
  };
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'AllChinaBuySpreadsheet Home', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: 'Competitor Comparison', item: siteUrl + pagePath },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={articleSchema} />
      <JsonLd data={breadcrumbSchema} />
      <Navbar />
      <header className="relative h-[420px] flex items-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Side%20by%20side%20comparison%20of%20different%20shopping%20platforms%20on%20multiple%20screens%2C%20modern%20tech%20office%2C%20bright%20clean%20lighting%2C%20competitive%20analysis%20theme%2C%20professional%20photography%2C%20sleek%20monitors%2C%20data%20visualization&width=1800&height=840&seq=vs-comp-hero-v3&orientation=landscape"
          alt="AllChinaBuySpreadsheet vs competitors"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/50"></div>
        <div className="relative z-10 w-full px-6 md:px-10 pt-16">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block px-3 py-1 bg-white/15 backdrop-blur-sm text-xs font-medium text-white rounded-full uppercase tracking-[0.15em] mb-5">Comparison</span>
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-white leading-tight">AllChinaBuySpreadsheet vs AliExpress, Taobao, DHgate &amp; 1688: Full Breakdown</h1>
          </div>
        </div>
      </header>
      <article className="py-16 md:py-24 px-6 md:px-10">
        <div className="max-w-3xl mx-auto">
          <p className="text-lg text-foreground-700 leading-relaxed mb-8">
            When it comes to buying products from China, consumers have no shortage of platforms to choose from. AliExpress,
            Taobao, DHgate, and 1688 each serve different segments of the market. But where does
            <strong className="text-primary-600 font-semibold"> AllChinaBuySpreadsheet</strong> fit in? This comprehensive
            comparison breaks down exactly how our spreadsheet-based approach stacks up against traditional marketplaces.
          </p>
          <h2 className="text-2xl font-heading font-bold text-foreground-950 mt-14 mb-5">Ease of Use: Curated vs Chaos</h2>
          <p className="text-base text-foreground-700 leading-relaxed mb-5">
            AliExpress offers a relatively user-friendly interface but still requires significant browsing through
            uncategorized listings. Taobao, while feature-rich, presents a steep learning curve with its Chinese-only
            interface and complex navigation. DHgate targets bulk buyers but its search functionality leaves much to
            be desired. 1688, designed for domestic Chinese wholesale, is nearly impossible for international buyers
            to navigate without a local agent. In contrast, the <strong className="text-primary-600 font-semibold">
            all china buy spreadsheet</strong> model eliminates browsing entirely — you see pre-organized, verified
            product data in a clean, comparable format instantly.
          </p>
          <h2 className="text-2xl font-heading font-bold text-foreground-950 mt-14 mb-5">Pricing Transparency</h2>
          <p className="text-base text-foreground-700 leading-relaxed mb-5">
            One area where <strong className="text-primary-600 font-semibold">best china spreadsheet products</strong> truly
            shines is pricing clarity. On platforms like AliExpress, the displayed price often excludes shipping, and
            variations in size or color can dramatically change the final cost. Our spreadsheet system shows all-in
            pricing with shipping estimates included, so there are no surprises at checkout. This transparency alone
            saves the average buyer 15-25% compared to discovering hidden costs after committing to a purchase.
          </p>
          <div className="mt-14 p-8 bg-primary-50 rounded-2xl text-center">
            <h3 className="text-xl font-heading font-bold text-foreground-950 mb-3">See the Difference Yourself</h3>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-6 py-3 bg-foreground-950 text-background-50 text-sm font-medium rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer">
                Explore Marketplace <i className="ri-arrow-right-line"></i>
              </a>
              <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 border border-foreground-200 text-sm font-medium text-foreground-700 rounded-full hover:bg-foreground-50 transition-colors whitespace-nowrap cursor-pointer">
                Back to Home <i className="ri-home-4-line"></i>
              </Link>
            </div>
          </div>
          <nav className="mt-12 pt-8 border-t border-background-200/70">
            <p className="text-xs font-medium text-foreground-500 uppercase tracking-wider mb-4">Related Articles</p>
            <ul className="space-y-2">
              <li><Link to="/allchinabuy-sheet-pricing" className="text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer">Pricing Strategy: How to Find the Best Deals &rarr;</Link></li>
              <li><Link to="/allchinabuy-sheet-guide" className="text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer">Shopping Guide: Save 70% on China Products &rarr;</Link></li>
              <li><Link to="/" className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors cursor-pointer">&larr; AllChinaBuySpreadsheet Home</Link></li>
            </ul>
          </nav>
        </div>
      </article>
      <Footer />
    </div>
  );
}